import { css, html, LitElement, nothing } from 'lit';

import '@brightspace-ui/core/components/button/button.js';
import '@brightspace-ui/core/components/button/button-subtle.js';
import '@brightspace-ui/core/components/dialog/dialog-confirm.js';
import '@brightspace-ui/core/components/loading-spinner/loading-spinner.js';
import '@brightspace-ui/core/components/status-indicator/status-indicator.js';
import '@brightspace-ui/core/components/inputs/input-select-styles.js';
import '@brightspace-ui/core/components/table/table-wrapper.js';
import { bodySmallStyles } from '@brightspace-ui/core/components/typography/styles.js';
import { selectStyles } from '@brightspace-ui/core/components/inputs/input-select-styles.js';
import { tableStyles } from '@brightspace-ui/core/components/table/table-wrapper.js';

class PMDemoResetPanel extends LitElement {

  static get properties() {
    return {
      environmentId: { type: String },
      tenantId: { type: String },
      client: { type: Object },
      _fixtures: { type: Array },
      _selectedFixture: { type: String },
      _isResetting: { type: Boolean },
      _currentExecution: { type: Object },
      _history: { type: Array },
      _loading: { type: Boolean },
      _elapsedTime: { type: String },
      _historyExpanded: { type: Boolean },
    };
  }

  static get styles() {
    return [
      selectStyles,
      bodySmallStyles,
      tableStyles,
      css`
        :host {
          display: block;
          padding: 1rem;
        }
        .reset-action {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          padding: 1rem 1.25rem;
          background: var(--d2l-color-regolith);
          border: 1px solid var(--d2l-color-mica);
          border-radius: 8px;
        }
        .reset-action-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .reset-action-info .label {
          font-size: 14px;
          font-weight: 650;
          color: var(--d2l-color-ferrite);
        }
        .reset-action-info .description {
          font-size: 13px;
          color: var(--d2l-color-tungsten);
          line-height: 1.45;
          max-width: 55ch;
        }
        .reset-action-meta {
          display: flex;
          gap: 1.5rem;
          margin-top: 8px;
        }
        .reset-action-meta .meta-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--d2l-color-tungsten);
        }
        .reset-action-meta .meta-value {
          font-weight: 600;
          color: var(--d2l-color-ferrite);
        }
        .reset-action-controls {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        }
        .progress-container {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          margin-bottom: 1.5rem;
          background: var(--d2l-color-celestine-plus-2);
          border: 1px solid var(--d2l-color-celestine);
          border-radius: 8px;
        }
        .progress-container.failed {
          background: var(--d2l-color-cinnabar-plus-2);
          border-color: var(--d2l-color-cinnabar);
        }
        .progress-container.completed {
          background: var(--d2l-color-olivine-plus-2);
          border-color: var(--d2l-color-olivine);
        }
        .history-section {
          margin-top: 1.5rem;
        }
        .history-header {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
        }
        .history-header h3 {
          margin: 0;
          font-size: 18px;
          letter-spacing: 0.2px;
        }
        .count-pill {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          background: #f3f4f6;
          color: var(--d2l-color-tungsten);
          border: 1px solid #e5e7eb;
        }
        .history-toggle {
          margin-left: auto;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 12px;
          color: var(--d2l-color-tungsten);
          padding: 4px 8px;
          border-radius: 4px;
        }
        .history-toggle:hover {
          background: #f3f4f6;
        }
        .stat-pills {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .stat-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: 999px;
          font-size: 11px;
          white-space: nowrap;
        }
        .stat-pill strong {
          font-weight: 650;
        }
        .stat-pill.users {
          background: rgba(59, 130, 246, 0.08);
          color: #2563eb;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }
        .stat-pill.apps {
          background: rgba(16, 185, 129, 0.08);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }
        .empty-state {
          padding: 2rem;
          text-align: center;
          color: var(--d2l-color-tungsten);
        }
      `,
    ];
  }

  constructor() {
    super();
    this._fixtures = [];
    this._selectedFixture = '';
    this._isResetting = false;
    this._currentExecution = null;
    this._history = [];
    this._loading = true;
    this._pollInterval = null;
    this._elapsedInterval = null;
    this._elapsedTime = '';
    this._historyExpanded = false;
  }

  async connectedCallback() {
    super.connectedCallback();
    await this._loadData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopPolling();
    this._stopElapsedTimer();
  }

  async _loadData() {
    this._loading = true;
    try {
      const [fixturesResult, historyResult] = await Promise.all([
        this.client.fetchDemoFixtures(this.environmentId),
        this.client.fetchDemoResetHistory(this.environmentId, this.tenantId),
      ]);
      this._fixtures = fixturesResult?.fixtures || [];
      this._selectedFixture = this._fixtures[0] || '';
      this._history = (historyResult?.executions || []).sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));

      const activeExecution = this._history.find(e => e.status === 'pending' || e.status === 'in_progress');
      if (activeExecution) {
        this._currentExecution = activeExecution;
        this._isResetting = true;
        this._startPolling(activeExecution.executionId);
      }
    } catch (err) {
      this._createAlert('error', `Failed to load demo reset data: ${err.message}`);
    }
    this._loading = false;
  }

  _onFixtureChange(e) {
    this._selectedFixture = e.target.value;
  }

  async _onResetClick() {
    const dialog = this.shadowRoot.getElementById('confirm-dialog');
    const action = await dialog.open();
    if (action !== 'yes') return;

    try {
      this._isResetting = true;
      const result = await this.client.startDemoReset(this.environmentId, this.tenantId, this._selectedFixture);
      this._currentExecution = { executionId: result.executionId, status: 'pending', fixture: this._selectedFixture };
      await this._refreshHistory();
      this._startPolling(result.executionId);
    } catch (err) {
      this._isResetting = false;
      this._createAlert('error', `Failed to start demo reset: ${err.message}`);
    }
  }

  _startPolling(executionId) {
    this._stopPolling();
    this._pollInterval = setInterval(() => this._pollStatus(executionId), 5000);
    this._startElapsedTimer();
  }

  _stopPolling() {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
    this._stopElapsedTimer();
  }

  _startElapsedTimer() {
    this._stopElapsedTimer();
    this._updateElapsedTime();
    this._elapsedInterval = setInterval(() => this._updateElapsedTime(), 1000);
  }

  _stopElapsedTimer() {
    if (this._elapsedInterval) {
      clearInterval(this._elapsedInterval);
      this._elapsedInterval = null;
    }
  }

  _updateElapsedTime() {
    const startedAt = this._currentExecution?.startedAt;
    if (!startedAt) {
      this._elapsedTime = '';
      return;
    }
    const ms = Date.now() - new Date(startedAt).getTime();
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) {
      this._elapsedTime = `${seconds}s`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      this._elapsedTime = `${minutes}m ${remainingSeconds}s`;
    }
  }

  async _pollStatus(executionId) {
    try {
      const status = await this.client.fetchDemoResetStatus(this.environmentId, executionId);
      this._currentExecution = status;

      if (status.status === 'completed') {
        this._stopPolling();
        this._isResetting = false;
        this._createAlert('success', 'Demo reset completed successfully');
        await this._refreshHistory();
        this.dispatchEvent(new CustomEvent('demo-reset-completed', {
          detail: { fixture: status.fixture, summary: status.summary },
          bubbles: true,
          composed: true,
        }));
      } else if (status.status === 'failed') {
        this._stopPolling();
        this._isResetting = false;
        this._createAlert('error', `Demo reset failed: ${status.error || 'Unknown error'}`);
        await this._refreshHistory();
      }
    } catch (err) {
      // Keep polling on transient errors
    }
  }

  async _refreshHistory() {
    try {
      const result = await this.client.fetchDemoResetHistory(this.environmentId, this.tenantId);
      this._history = (result?.executions || []).sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));
    } catch {
      // Silently fail history refresh
    }
  }

  _createAlert(type, message) {
    document.dispatchEvent(new CustomEvent('kraken-alert-event', {
      detail: { message, type },
      bubbles: true,
      composed: true,
    }));
  }

  _formatDate(isoString) {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  }

  _formatRelativeDate(isoString) {
    if (!isoString) return '-';
    const date = new Date(isoString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const daysDiff = Math.floor((today - target) / (1000 * 60 * 60 * 24));
    const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    if (daysDiff === 0) return `Today at ${time}`;
    if (daysDiff === 1) return `Yesterday at ${time}`;
    if (daysDiff < 7) return `${daysDiff} days ago`;
    if (daysDiff < 30) {
      const weeks = Math.floor(daysDiff / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    }
    return 'over a month ago';
  }

  _formatDuration(startedAt, completedAt) {
    if (!startedAt || !completedAt) return '-';
    const ms = new Date(completedAt) - new Date(startedAt);
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  _getStatusState(status) {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'alert';
      case 'in_progress': return 'default';
      case 'pending': return 'none';
      default: return 'none';
    }
  }

  _renderProgressBar() {
    if (!this._currentExecution || !this._isResetting) return nothing;

    const status = this._currentExecution.status;
    const statusClass = status === 'failed' ? 'failed' : status === 'completed' ? 'completed' : '';

    const fixture = this._currentExecution.fixture || this._selectedFixture;
    const statusLabel = status === 'pending' ? 'queued' : status === 'in_progress' ? 'in progress' : status;

    return html`
      <div class="progress-container ${statusClass}">
        ${status === 'pending' || status === 'in_progress' ? html`<d2l-loading-spinner size="36"></d2l-loading-spinner>` : nothing}
        <span>Resetting to <strong>${fixture}</strong> — ${statusLabel}${this._elapsedTime ? ` (${this._elapsedTime})` : ''}...</span>
      </div>
    `;
  }

  _renderDetails(execution) {
    if (execution.summary) {
      const s = execution.summary;
      const isPlg = s.onboardingReset !== undefined || s.applicationsDeleted !== undefined;
      if (isPlg) {
        return html`
          <div class="stat-pills">
            <span class="stat-pill apps"><strong>${s.applicationsDeleted || 0}</strong> apps removed</span>
            <span class="stat-pill apps"><strong>${s.goalsDeleted || 0}</strong> goals removed</span>
            ${s.onboardingReset ? html`<span class="stat-pill users">onboarding reset</span>` : nothing}
          </div>
        `;
      }
      return html`
        <div class="stat-pills">
          <span class="stat-pill users"><strong>${s.usersCreated || 0}</strong> users</span>
          <span class="stat-pill apps"><strong>${s.applicationsCreated || 0}</strong> apps</span>
        </div>
      `;
    }
    if (execution.error) {
      return html`<span style="color: var(--d2l-color-cinnabar)">${execution.error}</span>`;
    }
    return '-';
  }

  _renderHistoryTable() {
    if (this._history.length === 0) {
      return html`<div class="empty-state">No reset history found for this tenant.</div>`;
    }

    return html`
      <d2l-table-wrapper>
        <table class="d2l-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Preset</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            ${this._history.map(execution => html`
              <tr>
                <td>${this._formatDate(execution.startedAt)}</td>
                <td>${execution.fixture}</td>
                <td>
                  <d2l-status-indicator
                    state="${this._getStatusState(execution.status)}"
                    text="${execution.status}">
                  </d2l-status-indicator>
                </td>
                <td>${this._formatDuration(execution.startedAt, execution.completedAt)}</td>
                <td>${this._renderDetails(execution)}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </d2l-table-wrapper>
    `;
  }

  render() {
    if (this._loading) {
      return html`<d2l-loading-spinner size="50"></d2l-loading-spinner>`;
    }

    return html`
      <div class="reset-action">
        <div class="reset-action-info">
          <span class="label">Demo Preset</span>
          <span class="description">Replaces all tenant data with the selected preset. This is destructive and cannot be undone.</span>
          ${this._history.length > 0 ? html`
            <div class="reset-action-meta">
              <span class="meta-item">Current preset: <span class="meta-value">${this._history[0].fixture}</span></span>
              <span class="meta-item">Last reset: <span class="meta-value">${this._formatRelativeDate(this._history[0].completedAt || this._history[0].startedAt)}</span></span>
            </div>
          ` : nothing}
        </div>
        <div class="reset-action-controls">
          <select
            class="d2l-input-select"
            @change=${this._onFixtureChange}
            .value=${this._selectedFixture}
            ?disabled=${this._isResetting}>
            ${this._fixtures.map(f => html`<option value="${f}" ?selected=${f === this._selectedFixture}>${f}</option>`)}
          </select>
          <d2l-button
            primary
            ?disabled=${this._isResetting || !this._selectedFixture}
            @click=${this._onResetClick}>
            Reset Demo
          </d2l-button>
        </div>
      </div>

      ${this._renderProgressBar()}

      <div class="history-section">
        <div class="history-header" @click=${() => this._historyExpanded = !this._historyExpanded}>
          <h3>Reset History</h3>
          <span class="count-pill">${this._history.length}</span>
          <span class="history-toggle">${this._historyExpanded ? 'Hide' : 'Show'}</span>
        </div>
        ${this._historyExpanded ? this._renderHistoryTable() : nothing}
      </div>

      <d2l-dialog-confirm id="confirm-dialog"
        title-text="Confirm Demo Reset"
        text="This will delete all existing data and recreate from the selected preset. Continue?">
        <d2l-button slot="footer" primary data-dialog-action="yes">Yes, Reset</d2l-button>
        <d2l-button slot="footer" data-dialog-action="no">Cancel</d2l-button>
      </d2l-dialog-confirm>
    `;
  }
}

window.customElements.define('pm-demo-reset-panel', PMDemoResetPanel);
